import { useEffect, useState } from 'react';
import { listPayments } from '@/services/api/payments';
import type { Payment } from '@/types/payments';

export function PaymentHistory() {
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listPayments();
        if (!cancelled) setPayments(list);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-petit text-ink-muted">Couldn't load billing history.</p>;
  }

  if (!payments) {
    return <p className="text-petit text-ink-muted">Loading…</p>;
  }

  if (payments.length === 0) {
    return <p className="text-petit text-ink-muted">No payments yet.</p>;
  }

  return (
    <div className="divide-y divide-line-subtle">
      {payments.map((p) => (
        <div key={p.id} className="flex items-center justify-between py-2.5">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-primary capitalize">{p.plan}</span>
              <StatusBadge status={p.status} />
            </div>
            <p className="text-micro text-ink-faint">
              {new Date(p.created_at).toLocaleDateString()} · ref {p.provider_ref.slice(0, 12)}…
            </p>
          </div>
          <span className="text-petit text-ink-secondary">
            {formatAmount(p.amount_minor, p.currency)}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatAmount(minor: number, currency: string): string {
  const major = minor / 100;
  const sym = currency === 'NGN' ? '₦' : currency === 'USD' ? '$' : '';
  return `${sym}${major.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function StatusBadge({ status }: { status: Payment['status'] }) {
  const style =
    status === 'paid'
      ? 'border-success/30 bg-success/10 text-success'
      : status === 'failed'
        ? 'border-danger/30 bg-danger/10 text-danger'
        : 'border-line-default bg-clyra-raised text-ink-muted';
  return (
    <span className={`rounded-full border px-1.5 py-0.5 text-micro ${style}`}>{status}</span>
  );
}
